import { useWorkspaceNavigate } from '../../workspace-path';
import { Button, DropdownMenu, Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import { usePageHeader } from '../page-header';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The time section's header: a dropdown, not a title.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP HAS ONE `Időrögzítés` SIDEBAR ROW AND SWITCHES SECTION FROM THE HEADER; WE HAD
 * TWO SIDEBAR ROWS AND NO DROPDOWN.** `markup.html`'s header opens with
 * `<sc-if value="{{ showTimeMenu }}">` — on the time section it renders a `DropdownMenu` whose two
 * items are **Áttekintés** and **Időlogok**, each with a description, the current one checked.
 * `main.jsx` keeps that in a `timeSection` state variable.
 *
 * ⚠️ **Ours are two ROUTES rather than one variable, and that stays.** R-8 chose real addresses, so
 * `/time` and `/time/logs` are both bookmarkable and both audited by the reachability suite — which
 * a single screen rendering two bodies would not be (C16 records the same argument for the settings
 * rail). The dropdown navigates instead of setting state; the shape on screen is identical.
 *
 * ⚠️ **`/time/logs` left the sidebar because of this.** Two rows for one section is not what the
 * mockup draws, and the row was the only reason the header could stay a plain title.
 */
export function useTimeSectionHeader(
  section: 'overview' | 'logs',
  /**
   * ⚠️⚠️ **THE WORKSPACE-WIDE LOG LIST IS NOT IN THIS SECTION, SO IT DOES NOT CLAIM THIS
   * HEADER.** Both addresses render one screen, and a dropdown reading *Időrögzítés › Időlogok*
   * above everybody's hours would offer to switch to an *Áttekintés* that is not the sibling of
   * what the reader is looking at. `false` makes the claim inert rather than writing an empty
   * header — `usePageHeader` argues why those are different.
   */
  enabled = true,
): void {
  const t = useT();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();

  usePageHeader(
    () => (
      <DropdownMenu
        items={[
          { type: 'label', label: t('time.section.group') },
          {
            label: t('time.section.overview'),
            description: t('time.section.overviewDesc'),
            icon: 'calendar-days',
            checked: section === 'overview',
            onSelect: () => {
              go('/time');
            },
          },
          {
            label: t('time.section.logs'),
            description: t('time.section.logsDesc'),
            icon: 'list-checks',
            checked: section === 'logs',
            onSelect: () => {
              go('/time/logs');
            },
          },
        ]}
        /* 300px and left-aligned — the mockup's own `width="300" align="start"`. */
        width={300}
        align="start"
        label={t('time.section.group')}
      >
        {/* ⚠️ `-ml-2` pulls the ghost button's padding back so its LABEL lines up with the plain
            title on every other screen, rather than the button's edge. The mockup's own class. */}
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5 px-2 text-[13.5px] font-medium">
          <Icon
            name={section === 'logs' ? 'list-checks' : 'calendar-days'}
            size={15}
            className="text-muted-foreground"
          />
          {section === 'logs' ? t('time.section.logs') : t('time.section.overview')}
          <Icon name="chevron-down" size={13} className="opacity-60" />
        </Button>
      </DropdownMenu>
    ),
    /* ⚠️ Values only — see `usePageHeader`. `navigate` and `t` are reached through the ref. */
    [section],
    enabled,
  );
}
